import React from 'react';
import { css } from '@emotion/core';

import { gray5 } from 'styles/global_defaults/colors';
import { mobile } from 'styles/global_defaults/media-queries';
import ClickableContainer, {
  ClickableContainerProps,
} from 'components/clickable-container';

export const CARD_WIDTH = 280;
const MOBILE_CARD_WIDTH = 230;

type RenderProp<T> = (data?: T) => React.ReactNode;

type Props = ClickableContainerProps & {
  renderHeader: RenderProp<boolean>,
  renderContent: RenderProp<boolean>,
  className?: string,
  headerClassName?: string,
  contentClassName?: string,
  style?: React.CSSProperties,
  backdropLayerClassName?: string,
  renderExtraContent?: RenderProp<any>,
  isFocusable?: boolean,
};

const BaseCard = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    style,
    className,
    renderHeader,
    renderContent,
    headerClassName,
    contentClassName,
    renderExtraContent,
    backdropLayerClassName,
    ...restProps
  } = props;

  const [isMouseIn, setIsMouseIn] = React.useState(false);

  const isResponsive = !style?.width;

  const styles = css`
    height: 280px;
    display: flex;
    flex-direction: column;
    border: 1px solid ${gray5};
    max-width: ${CARD_WIDTH}px;
    box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.1);

    ${isResponsive ? css`
      width: ${CARD_WIDTH}px;

      ${mobile(css`
        width: ${MOBILE_CARD_WIDTH}px;
      `)};
    ` : mobile(css`
      min-width: ${MOBILE_CARD_WIDTH}px;
    `)};

    .card-header {
      height: 100px;
      flex-shrink: 0;
      position: relative;
      box-sizing: content-box;

      .backdrop-layer {
        width: 100%;
        height: 100%;
        position: absolute;
      }
    }

    .card-content {
      flex: 1;
    }
  `;

  const handleMouseEnter = () => setIsMouseIn(true);
  const handleMouseLeave = () => setIsMouseIn(false);

  return (
    <ClickableContainer
      ref={ref}
      css={styles}
      style={style}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...restProps}
    >
      <div
        className={`card-header${headerClassName ? ` ${headerClassName}` : ''}`}
      >
        <div className={`backdrop-layer${backdropLayerClassName ? ` ${backdropLayerClassName}` : ''}`}>
          {renderHeader(isMouseIn)}
        </div>
      </div>
      <div className={`card-content${contentClassName ? ` ${contentClassName}` : ''}`}>
        {renderContent(isMouseIn)}
      </div>
      {renderExtraContent?.()}
    </ClickableContainer>
  );
});

export default BaseCard;
